import type { RegisteredTenant } from '@festina/configuration-general-types/configuration'
import { LayerKey } from '@festina-life/api'
import { type Optional, typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { FormikForm, FormikSelect } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Modal } from '@festina-life/ui/structure'
import { useId } from 'react'
import { useNavigate } from 'react-router'

import { server as serverPath } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useConciseBranch } from '../../uiModels/branches/useConciseBranch'
import { useAllowedTenants } from '../../uiModels/tenants/useAllowedTenants'

type ChangeTenantInput = {
  tenant: Optional<RegisteredTenant>
}

const validate = typeUtils.createValidator<ChangeTenantInput>()

/**
 * Component for changing the current tenant if the user doesn't have privileges to read the current tenant.
 * In the future this will probably have to be extended to layers in general.
 */
export const InvalidTenantModal = () => {
  const translate = useTranslate()
  const formId = useId()
  const { branch, server, block, layer: currentLayer } = useNavigationParams()
  const currentTenant = currentLayer.tenantId
  const conciseBranch = useConciseBranch({
    branchName: branch,
    server,
    shouldFallbackToStandard: false,
  })
  const { data, isLoading } = useAllowedTenants({
    branchKey:
      conciseBranch && branch && server ? { name: branch, server } : undefined,
    block,
  })
  const navigate = useNavigate()

  const isCurrentTenantAvailable =
    !currentTenant ||
    (!!currentTenant &&
      data.tenants?.find(
        (registeredTenant) => registeredTenant.tenantId === currentTenant,
      ))

  const initialValues = {
    tenant: currentTenant
      ? data.tenants?.find(
          (registeredTenant) => registeredTenant.tenantId === currentTenant,
        )
      : null,
  }

  const handleSubmit = async (values: ChangeTenantInput) => {
    await navigate(
      serverPath({
        server,
        block,
        layer: new LayerKey({
          tenantId: values.tenant?.tenantId,
          layerId: currentLayer.layerId,
        }),
        branch,
      }),
    )
  }

  return conciseBranch && branch && !isLoading && !isCurrentTenantAvailable ? (
    <Modal isOpen onClose={() => null}>
      <Modal.Title
        title={translate('invalidTenantModal.title', {
          tenantId: currentTenant || translate('tenant.null'),
        })}
      />
      <Modal.Body outerClassName="max-w-sm">
        <p>
          {translate('invalidTenantModal.description', { branchName: branch })}
        </p>

        <FormikForm
          initialValues={initialValues}
          formId={formId}
          onSubmit={handleSubmit}
        >
          <FormikSelect
            options={data.tenants ?? []}
            label={translate('invalidTenantModal.form.tenant.label')}
            name={validate('tenant')}
            placeholder={translate('tenant.null')}
            renderFn={(tenant) => tenant.tenantName ?? tenant.tenantId ?? ''}
            keyFn={(tenant) => tenant.tenantId ?? ''}
            sort={false}
          />
        </FormikForm>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" type="submit" form={formId}>
          {translate('invalidTenantModal.submitButton.label')}
        </Button>
      </Modal.Footer>
    </Modal>
  ) : null
}
